import { Breadcrumb, CardContainer, Container } from "components";
import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { API_URL_agent, API_URL_organization } from "constants/api";
import { agentReducers } from "redux/agentSlice";
import { getData, postData, putData } from "actions";
import { organizationReducers } from "redux/organizationSlice";

const FormUser = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const { getOrganizationResult } = useSelector((state) => ({
    getOrganizationResult: state.organization.getOrganizationResult,
  }));

  const getOrganization = useCallback(
    async (params) => {
      getData(
        API_URL_organization,
        params,
        { dispatch, redux: organizationReducers },
        "GET_ORGANIZATION"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      username: location.state ? location.state.item.username : "",
      first_name: location.state ? location.state.item.first_name : "",
      last_name: location.state ? location.state.item.last_name : "",
      email: location.state ? location.state.item.email : "",
      ext: location.state ? location.state.item.ext : "",
      country_code: location.state ? location.state.item.country_code : "",
      phone_number: location.state ? location.state.item.phone_number : "",
      mobile: location.state ? location.state.item.mobile : "",
      primary_dpt: location.state ? location.state.item.primary_dpt : "",
      agent_tzone: location.state ? location.state.item.agent_tzone : "",
      is_active: location.state ? location.state.item.is_active : false,
      ban: location.state ? location.state.item.ban : 0,
      send_password_via_email: false,
      organization: location.state ? location.state.item.organization : "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Username is required"),
      first_name: Yup.string().required("Firstname is required"),
      email: Yup.string().required("Email is required"),
      is_active: Yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_agent,
          formData,
          { dispatch, redux: agentReducers },
          "ADD_AGENT",
          () => {
            navigate("/users/userdirectory");
          }
        );
      } else {
        putData(
          `${API_URL_agent}${location.state.item.id}/`,
          formData,
          { dispatch, redux: agentReducers },
          "ADD_AGENT",
          () => {
            navigate("/users/userdirectory");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    const params = `?limit=${9999999}`;
    getOrganization(params);
  }, []);

  return (
    <Container>
      <Breadcrumb title={"Form User Directory"} excludedPaths={["users"]} />
      <CardContainer
        title={typeForm === "create" ? "Create User" : "Edit User"}
      >
        <form onSubmit={handleFormSubmit}>
          <div className="m-5">
            <div className="grid grid-cols-12 grid-flow-row mb-4 gap-4">
              <div className=" col-span-12 md:col-span-4">
                <div className="grid grid-cols-12">
                  <label className="col-span-12 font-bold">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="first_name"
                    type="text"
                    name="first_name"
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                {formik.touched.first_name && formik.errors.first_name && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.first_name}
                  </div>
                )}
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">Last Name</label>
                  <input
                    id="last_name"
                    type="text"
                    name="last_name"
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.last_name && formik.errors.last_name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.last_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">
                    User Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.username && formik.errors.username && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.username}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 grid-flow-row mb-4 gap-4">
              <div className="col-span-12 md:col-span-6">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.email}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">Phone Number</label>
                  <input
                    id="phone_number"
                    type="number"
                    name="phone_number"
                    ref={(ref) => (inputRefs.current[4] = ref)}
                    value={formik.values.phone_number}
                    onChange={formik.handleChange}
                    className="p-1 w-full col-span-12 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 grid-flow-row mb-4 gap-4">
              <div className="col-span-12 md:col-span-6 lg:col-span-2">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">Status</label>
                  <div className="flex flex-col">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        id="is_active"
                        name="is_active"
                        checked={formik.values.is_active === true}
                        onChange={() => formik.setFieldValue("is_active", true)}
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">Active</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        id="is_active"
                        name="is_active"
                        checked={formik.values.is_active === false}
                        onChange={() =>
                          formik.setFieldValue("is_active", false)
                        }
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">Non-Active</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-2">
                <div className="grid grid-rows-12">
                  <label className="col-span-12 font-bold">Banned</label>
                  <div className="flex flex-col">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        id="ban"
                        name="ban"
                        checked={formik.values.ban === 1}
                        onChange={() => formik.setFieldValue("ban", 1)}
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">Active</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        id="ban"
                        name="ban"
                        checked={formik.values.ban === 0}
                        onChange={() => formik.setFieldValue("ban", 0)}
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">Non-Active</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-12 mb-4">
              <label className="col-span-12 font-bold">Internal Note</label>
              <div className="col-span-12">
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.agent_tzone}
                  onChange={(e) =>
                    formik.setFieldValue("agent_tzone", e.editor.getData())
                  }
                />
              </div>
            </div>
          </div>
          <div className="h-14 w-full flex items-center px-5 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="rounded-md text-white py-1.5 w-32 bg-sky-700 hover:bg-sky-800 "
            >
              Submit
            </button>
          </div>
        </form>
      </CardContainer>
    </Container>
  );
};

export default FormUser;
